import { type FormEvent, useEffect, useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppContext } from "../app/AppContext";
import { ApiError } from "../core/api-client";
import type { MessageId } from "../core/messages";
import { parseStarterHandoff } from "../core/starter-handoff";
import { BrandMark } from "../shared/Shared";
import styles from "./AuthGate.module.css";
import { AuthFlowError } from "./auth-errors";

/**
 * The sign-in screen. Route/gate orchestration lives in App.tsx: this renders
 * at /login and /register and IN PLACE at any member URL a signed-out visitor
 * opens, so deep links survive sign-in unchanged.
 *
 * MFA reproduces the V1 card (centered 420px card, Sign in / Create account
 * pill tabs, email+password with Show/Hide, forgot-password, Google); LYA
 * keeps its two-panel composition with the same capabilities.
 */

type ScreenMode = "signin" | "create" | "reset";

type SentNotice = { kind: "reset-sent"; email: string } | null;

function useAuthScreen() {
  const context = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const routeMode: ScreenMode | null =
    location.pathname === "/register" ? "create" : location.pathname === "/login" ? "signin" : null;
  const [override, setOverride] = useState<ScreenMode | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset the local override whenever the route decides the mode
  useEffect(() => setOverride(null), [location.pathname]);
  // MFA splash Step-1 handoff: rendered in place at /start with a valid
  // handoff in location.state, the card defaults to Create Account (the
  // Sign in tab stays available). Mode switches here only setOverride —
  // no navigation — so the router state survives for the wizard.
  const deepLinkMode: ScreenMode =
    parseStarterHandoff(location.state) !== null ? "create" : "signin";
  const mode: ScreenMode = override ?? routeMode ?? deepLinkMode;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState<"password" | "google" | "reset" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [sent, setSent] = useState<SentNotice>(null);
  const configured = Boolean(context.runtimeConfig.firebase);

  function setMode(next: ScreenMode) {
    setError(null);
    setSent(null);
    // On the canonical routes the tabs navigate (so /register is linkable and
    // Back works); rendered in place at a deep link they only switch locally,
    // preserving the URL the member tree will resolve after sign-in. The
    // search string rides along so a preserved ?offer=<id> (pricing → register
    // handoff) survives switching between the tabs.
    if (next !== "reset" && routeMode && next !== routeMode) {
      navigate({
        pathname: next === "create" ? "/register" : "/login",
        search: location.search,
      });
      return;
    }
    setOverride(next);
  }

  async function run(kind: NonNullable<typeof busy>, work: () => Promise<void>) {
    setBusy(kind);
    setError(null);
    try {
      await work();
    } catch (nextError) {
      setError(nextError);
    } finally {
      setBusy(null);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (mode === "reset") {
      void run("reset", async () => {
        await context.sendPasswordReset(email.trim());
        // Enumeration-safe: the same acknowledgement whether or not the
        // address has an account.
        setSent({ kind: "reset-sent", email: email.trim() });
      });
      return;
    }
    void run("password", async () => {
      if (mode === "create") {
        const ownerName = name.trim().slice(0, 80);
        if (!ownerName) throw new AuthFlowError("enter_name");
        await context.registerWithPassword({ name: ownerName, email: email.trim(), password });
      } else {
        await context.signInWithPassword(email.trim(), password);
      }
    });
  }

  function onGoogle() {
    void run("google", () => context.signInWithGoogle());
  }

  return {
    intl,
    mode,
    setMode,
    name,
    setName,
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    busy,
    error,
    sent,
    configured,
    onSubmit,
    onGoogle,
  };
}

type AuthScreenState = ReturnType<typeof useAuthScreen>;

/** V1's coral error box; AuthFlowError codes map to auth.err.*, API errors to error.*. */
export function AuthErrorBox({ error }: { error: unknown }) {
  const intl = useIntl();
  if (!error) return null;
  let id: MessageId = "auth.error";
  if (error instanceof AuthFlowError) {
    const candidate = `auth.err.${error.code}` as MessageId;
    if (intl.messages[candidate]) id = candidate;
  } else if (error instanceof ApiError) {
    const candidate = `error.${error.code}` as MessageId;
    if (intl.messages[candidate]) id = candidate;
  }
  return (
    <div className={styles.errorBox} role="alert">
      <FormattedMessage id={id} />
    </div>
  );
}

/** Shared password input with the V1 Show/Hide toggle (also the action handler's new-password field). */
export function PasswordField({
  value,
  onChange,
  show,
  onToggleShow,
  disabled,
  autoComplete,
  labelId,
}: {
  value: string;
  onChange: (value: string) => void;
  show: boolean;
  onToggleShow: () => void;
  disabled: boolean;
  autoComplete: "current-password" | "new-password";
  labelId?: MessageId;
}) {
  const intl = useIntl();
  const inputId = useId();
  // Explicit htmlFor (not a wrapping label): the Show/Hide button must not
  // leak into the input's accessible name.
  return (
    <div className={styles.cardLabel}>
      <label htmlFor={inputId}>
        <FormattedMessage id={labelId ?? "auth.passwordLabel"} />
      </label>
      <span className={styles.passwordWrap}>
        <input
          id={inputId}
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          required
          minLength={6}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={intl.formatMessage({ id: "auth.passwordPlaceholder" })}
          disabled={disabled}
        />
        {/* V1: Show/Hide stays clickable while busy. */}
        <button
          type="button"
          className={styles.showHide}
          title={intl.formatMessage({ id: "auth.password.toggleTitle" })}
          aria-pressed={show}
          onClick={onToggleShow}
        >
          <FormattedMessage id={show ? "auth.password.hide" : "auth.password.show"} />
        </button>
      </span>
    </div>
  );
}

function NameField({ state }: { state: AuthScreenState }) {
  const { intl, name, setName, configured, busy } = state;
  return (
    <label className={styles.cardLabel}>
      <FormattedMessage id="auth.nameLabel" />
      <input
        type="text"
        autoComplete="name"
        required
        maxLength={80}
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder={intl.formatMessage({ id: "auth.namePlaceholder" })}
        disabled={!configured || busy !== null}
      />
    </label>
  );
}

function EmailField({ state }: { state: AuthScreenState }) {
  const { intl, email, setEmail, configured, busy } = state;
  const { experience } = useAppContext();
  const suffix = experience.id === "myfirstagent" ? "mfa" : "lya";
  return (
    <label className={styles.cardLabel}>
      <FormattedMessage id={`auth.emailLabel.${suffix}`} />
      <input
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder={intl.formatMessage({ id: "auth.emailPlaceholder" })}
        disabled={!configured || busy !== null}
      />
    </label>
  );
}

function SentNotices({ state }: { state: AuthScreenState }) {
  const { sent, configured } = state;
  return (
    <>
      {sent?.kind === "reset-sent" ? (
        <p className={styles.notice} role="status">
          <FormattedMessage id="auth.reset.sent" values={{ email: sent.email }} />
        </p>
      ) : null}
      {!configured ? (
        <p className={`${styles.notice} ${styles.configError}`} role="alert">
          <FormattedMessage id="auth.configMissing" />
        </p>
      ) : null}
    </>
  );
}

/**
 * The V1 sign-in card, rebuilt from index.html. Both sites render it inside
 * PublicShell (LYA's full-bleed two-panel screen retired with the 2026-07
 * corporate rebuild; the shell carries the signed-out locale switcher).
 */
export function SignInScreen() {
  const state = useAuthScreen();
  const { intl, mode, setMode, busy, error, configured } = state;
  const { experience } = useAppContext();
  const suffix = experience.id === "myfirstagent" ? "mfa" : "lya";
  return (
    // PublicShell provides the <main> landmark — this is just layout.
    <div className={styles.cardPage}>
      <section className={styles.card} aria-labelledby="sign-in-title">
        <div className={styles.cardLogo}>
          <BrandMark label={intl.formatMessage({ id: "auth.tab.signIn" })} />
        </div>
        <h1 id="sign-in-title" className={styles.cardTitle}>
          <FormattedMessage
            id={
              mode === "create"
                ? "auth.createTitle"
                : mode === "reset"
                  ? "auth.reset.title"
                  : "auth.welcomeTitle"
            }
          />
        </h1>
        <p className={styles.cardSub}>
          <FormattedMessage
            id={
              mode === "create"
                ? "auth.createSub"
                : mode === "reset"
                  ? "auth.reset.sub"
                  : "auth.welcomeSub"
            }
          />
        </p>

        {mode !== "reset" ? (
          <div
            className={styles.modeToggle}
            role="tablist"
            aria-label={intl.formatMessage({ id: "auth.tab.signIn" })}
          >
            <button
              type="button"
              role="tab"
              aria-selected={mode === "signin"}
              className={mode === "signin" ? styles.modeActive : ""}
              onClick={() => setMode("signin")}
            >
              <FormattedMessage id="auth.tab.signIn" />
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "create"}
              className={mode === "create" ? styles.modeActive : ""}
              onClick={() => setMode("create")}
            >
              <FormattedMessage id="auth.tab.create" />
            </button>
          </div>
        ) : null}

        <form onSubmit={state.onSubmit}>
          <div className={styles.fields}>
            {mode === "create" ? <NameField state={state} /> : null}
            <EmailField state={state} />
            {mode !== "reset" ? (
              <PasswordField
                value={state.password}
                onChange={state.setPassword}
                show={state.showPassword}
                onToggleShow={() => state.setShowPassword(!state.showPassword)}
                disabled={!configured || busy !== null}
                autoComplete={mode === "create" ? "new-password" : "current-password"}
              />
            ) : null}
          </div>
          {mode === "signin" ? (
            <div className={styles.forgotRow}>
              <button type="button" className={styles.forgotLink} onClick={() => setMode("reset")}>
                <FormattedMessage id="auth.forgot" />
              </button>
            </div>
          ) : null}
          <AuthErrorBox error={error} />
          <button
            className={styles.primaryCta}
            type="submit"
            disabled={!configured || busy !== null}
          >
            {busy === "password" || busy === "reset" ? (
              <FormattedMessage id="auth.busy" />
            ) : (
              <FormattedMessage
                id={
                  mode === "create"
                    ? "auth.cta.create"
                    : mode === "reset"
                      ? "auth.reset.cta"
                      : "auth.cta.signIn"
                }
              />
            )}
          </button>
        </form>

        {mode === "create" ? (
          <div className={styles.verifyNotice}>
            <FormattedMessage id="auth.verifyNotice" />
          </div>
        ) : null}

        {mode === "reset" ? (
          <>
            <SentNotices state={state} />
            <p className={styles.footnote}>
              <button type="button" className={styles.backLink} onClick={() => setMode("signin")}>
                <FormattedMessage id="auth.reset.back" />
              </button>
            </p>
          </>
        ) : (
          <>
            <div className={styles.cardDivider}>
              <FormattedMessage id="auth.or" />
            </div>
            <button
              className={styles.secondaryCta}
              type="button"
              onClick={state.onGoogle}
              disabled={!configured || busy !== null}
            >
              {busy === "google" ? (
                <FormattedMessage id="auth.busy" />
              ) : (
                <FormattedMessage id="auth.google" />
              )}
            </button>
            <SentNotices state={state} />
            <p className={styles.footnote}>
              <FormattedMessage id={`auth.kidsNote.${suffix}`} />
            </p>
            <p className={styles.footnote}>
              <FormattedMessage id="auth.required" />
            </p>
          </>
        )}
      </section>
    </div>
  );
}
